import { NO_PROJECT_SELECTED } from "@/components/projects/ProjectSelect";
import { type EnvironmentClassEntry } from "@/hooks/use-environment-class-entries";
import { type PlainProject, type PlainProjectEnvironmentClass } from "@/queries/project-queries";
import { getPreferredLocalEnvironmentClass } from "@/utils/test/api/environment-classes";
import { RunnerPhase } from "gitpod-next-api/gitpod/v1/runner_pb";
import { useEffect } from "react";

export type UseEnvironmentClassSelectionProps = {
    // Loading states
    isLoadingEnvClasses: boolean;
    isLoadingProjectClasses?: boolean;
    isLoadingProjects?: boolean;
    isValidContext?: boolean;

    // Data
    selectedProject?: PlainProject | typeof NO_PROJECT_SELECTED | undefined;
    selectedClassEntry: EnvironmentClassEntry | undefined;
    sortedEnvironmentClassEntries: EnvironmentClassEntry[];
    projectEnvironmentClasses?:
        | { projectId: string | undefined; environmentClasses: PlainProjectEnvironmentClass[] }
        | undefined;
    selectedAuthClassEntry?: EnvironmentClassEntry | undefined;

    // Conditions
    isAuthenticationRequired?: boolean | undefined;

    // Setter
    setSelectedClassEntry: (classEntry?: EnvironmentClassEntry) => void;
};

/**
 * Manages environment class selection lifecycle with proper loading state coordination.
 *
 * This hook handles three scenarios:
 * 1. Default class selection when no project is selected
 * 2. Project-specific class selection when a project is chosen
 * 3. Auth-required class selection for authentication flows
 */
export function useEnvironmentClassSelection({
    isLoadingEnvClasses,
    isLoadingProjectClasses = false,
    isValidContext = false,
    isLoadingProjects = false,
    selectedProject,
    selectedClassEntry,
    sortedEnvironmentClassEntries,
    projectEnvironmentClasses,
    selectedAuthClassEntry,
    isAuthenticationRequired,
    setSelectedClassEntry,
}: UseEnvironmentClassSelectionProps) {
    const hasProject = selectedProject && selectedProject !== NO_PROJECT_SELECTED;

    // Select auth-required class when authentication is needed
    useEffect(() => {
        if (isAuthenticationRequired && selectedAuthClassEntry) {
            setSelectedClassEntry(selectedAuthClassEntry);
        }
    }, [isAuthenticationRequired, selectedAuthClassEntry, setSelectedClassEntry]);

    // Select default class when no project is selected
    useEffect(() => {
        // Block on critical loading states that must complete before we can make a selection.
        // This also blocks on project loading states, so that we can allow the second effect to take over the selection if a project ends up being selected.
        if (isLoadingEnvClasses || !isValidContext || isLoadingProjects) {
            return;
        }

        // Block if a project is selected, or project selection is still pending (undefined). Project class selection is handled in the next effect.
        if (selectedProject !== NO_PROJECT_SELECTED) {
            return;
        }

        // Check if we currently have a valid selection that exists in the list
        const isCurrentSelectionValid =
            selectedClassEntry &&
            sortedEnvironmentClassEntries.some((v) => v.clazz?.id === selectedClassEntry.clazz?.id);

        if (isCurrentSelectionValid) {
            return;
        }

        // Only select default if nothing is selected yet (or if the current selection is not valid)
        if (sortedEnvironmentClassEntries.length > 0) {
            const firstActive = sortedEnvironmentClassEntries.find(
                (et) => !et.policyRestricted && et.runner?.status?.phase === RunnerPhase.ACTIVE,
            );
            if (firstActive) {
                setSelectedClassEntry(firstActive);
            }
        }
    }, [
        isLoadingEnvClasses,
        isValidContext,
        isLoadingProjectClasses,
        selectedClassEntry,
        sortedEnvironmentClassEntries,
        setSelectedClassEntry,
        hasProject,
        isLoadingProjects,
        selectedProject,
    ]);

    // Select project-specific class when project is chosen
    useEffect(() => {
        if (isLoadingProjectClasses || !projectEnvironmentClasses) {
            return;
        }

        if (!hasProject) {
            return;
        }

        const isAllowedByProject = (entry: EnvironmentClassEntry) => {
            return projectEnvironmentClasses.environmentClasses.some(
                (pec) =>
                    (pec.environmentClass.case === "environmentClassId" &&
                        pec.environmentClass.value === entry.clazz.id) ||
                    (pec.environmentClass.case === "localRunner" && entry.localRunner),
            );
        };

        if (selectedClassEntry && isAllowedByProject(selectedClassEntry)) {
            return; // Current selection is valid for this project. Keep it.
        }

        if (sortedEnvironmentClassEntries.length > 0) {
            if (selectedClassEntry) {
                // We already have a class selected. Is it acceptable for the project?
                if (isAllowedByProject(selectedClassEntry)) {
                    return;
                }
            }
            // We don't have a class or it's not compatible with the project's classes. Let's select the first class offered by the project
            let envClassEntry: EnvironmentClassEntry | undefined;
            const projectEnvironmentClass = projectEnvironmentClasses.environmentClasses[0];
            if (projectEnvironmentClass) {
                if (projectEnvironmentClass.environmentClass.case === "localRunner") {
                    envClassEntry = getPreferredLocalEnvironmentClass(sortedEnvironmentClassEntries);
                } else {
                    envClassEntry = sortedEnvironmentClassEntries.find(
                        (et) => et.clazz.id === projectEnvironmentClass.environmentClass.value,
                    );
                    // todo(ft): should we error out here if the class is not found? We could possibly be running this indefinitely otherwise, in a scenario where a user has access to a project but not the environment class.
                }
            }
            if (envClassEntry) {
                setSelectedClassEntry(envClassEntry);
            }
        }
    }, [
        hasProject,
        projectEnvironmentClasses,
        isLoadingProjectClasses,
        sortedEnvironmentClassEntries,
        setSelectedClassEntry,
        selectedClassEntry,
    ]);
}
